// lib
import React, { useCallback, useRef } from 'react'
import { useHistory } from 'react-router-dom'

// components
import BackbuttonHeader from 'src/components/header/common/backbutton_header/backbutton_header'
import MetaTags from 'src/components/meta_tags/meta_tags'
import PageTitleFollowers from 'src/containers/smart_components/page_title/page_title_followers'
import ProfileFollowers from 'src/containers/smart_components/profile_followers/profile_followers'
import LayoutNoMap from 'src/layout/layout_no_map'

// constants & interfaces
import {
  PROFILE_FOLLOWERS_TITLE,
  PROFILE_OG_TYPE
} from 'src/constants/meta_tags'
import { textResources } from 'src/lang/de'

// helpers
import { useFollowerList } from '@lopo-tech/lopo-api'
import { PROFILES_LIST_PER_PAGE } from 'src/constants/profile_list'
import { useGetProfileIdWithTypeFromUrlParams } from 'src/hooks_shared/use_url_params'
import { usePageTracking } from 'src/pages/hooks/hooks'
import { useSelectUser } from 'src/reducers/user/user_hooks'
import { isPrivateProfile } from 'src/utils/profile'
import { UrlUtils } from 'src/utils/url/url'

const ProfileFollowersPage: React.FC = () => {
  const history = useHistory()
  const user = useSelectUser()

  const { slug, profileType } = useGetProfileIdWithTypeFromUrlParams()

  const { data, fetchMore, loading } = useFollowerList({
    variables: {
      slug,
      type: profileType,
      page: 1,
      perPage: PROFILES_LIST_PER_PAGE
    },
    // TODO: we should refetch on mutation instead, but that currently does not work properly , so instead we don't rely purely on the cache for this query
    fetchPolicy: 'cache-and-network'
  })

  const profile = data?.profile

  // we need to ensure that the callback always has the latest loading state
  // to avoid multiple fetchMore calls being triggered due to the getNextPage
  // being in a throttled fashion
  const loadingRef = useRef(loading)
  loadingRef.current = loading
  const getNextPage = useCallback(() => {
    if (
      profile?.__typename !== 'PrivateProfile' &&
      profile?.followers?.pageInfo.nextPage &&
      !loadingRef.current
    ) {
      fetchMore({
        variables: { page: profile.followers.pageInfo.nextPage }
      })
    }
  }, [
    profile?.__typename !== 'PrivateProfile' &&
      profile?.followers?.pageInfo.nextPage,
    fetchMore
  ])
  const currentPage =
    (profile?.__typename !== 'PrivateProfile' &&
    profile?.followers?.pageInfo.previousPage
      ? profile.followers.pageInfo.previousPage
      : 0) + 1

  const { address, name, description, avatar } = profile || {}

  usePageTracking({ pageTitle: PROFILE_FOLLOWERS_TITLE, user })

  const handleBackButton = useCallback(() => {
    UrlUtils.goBack(
      history,
      profile
        ? UrlUtils.getProfileFrontendPath(profile.slug, profile?.__typename)
        : undefined
    )
  }, [history, profile])

  return (
    <>
      {profile && isPrivateProfile(profile.__typename)
        ? (
          // we currently do not show any meta tag information for PrivateProfiles
          <MetaTags title={PROFILE_FOLLOWERS_TITLE} />
        )
        : (
          <MetaTags
            author={name}
            canonical={UrlUtils.currentUrlWithHostAndPath()}
            description={description}
            geo_placename={address?.locationShape.name}
            geo_position={
              address?.locationShape &&
              `${address.locationShape.center.latitude};${address.locationShape.center.longitude}`
            }
            og_image={avatar?.previewUrl}
            og_url={UrlUtils.currentUrlWithHostAndPath()}
            og_longitude={address?.locationShape?.center.longitude.toString()}
            og_latitude={address?.locationShape?.center.latitude.toString()}
            og_type={PROFILE_OG_TYPE}
            profile_username={name}
            title={PROFILE_FOLLOWERS_TITLE}
          />
        )}
      <LayoutNoMap
        pageTitle={<PageTitleFollowers name={profile?.name} />}
        subHeader={
          <BackbuttonHeader
            title={textResources.pageTitles.followers}
            handleBackButton={handleBackButton}
          />
        }
        withHeaderMargin
      >
        <ProfileFollowers
          currentPage={currentPage}
          getNextPage={getNextPage}
          isLoading={loading}
          profileWithFollowers={profile}
        />
      </LayoutNoMap>
    </>
  )
}

export default ProfileFollowersPage
